import { httpClient } from "../http-client";
import { buildQueryString } from "./query-builder";
import { ApiResponse } from "../../types/responses/api-response";
import type {
  CreateEventNotificationDto as GeneratedCreateEventNotificationDto,
  EventAdminGetModerationItemsData,
  EventAdminGetNotificationsData,
  EventAdminGetParticipantsData,
  EventAdminGetTeamData,
  EventAdminMetricsDataDto,
  EventAdminModerationItemResponseDto,
  EventAdminModerationResponseDto,
  EventAdminNotificationResponseDto,
  EventAdminNotificationsResponseDto,
  EventAdminParticipantResponseDto,
  EventAdminTeamMemberResponseDto,
  EventAdminTeamResponseDto,
  ModerateContentDto,
  UpdateParticipantStatusDto as GeneratedUpdateParticipantStatusDto,
} from "../../api/generated/types.gen";

// ===== EVENT ADMIN API TYPES =====

/**
 * AD-188. Участник события — генерат BE-контракта, а не рукописное зеркало.
 *
 * Прежнее зеркало описывало вложенный `user` (`user.firstName`, `user.email`),
 * которого сервер не отдаёт: ответ плоский (`name`, `lastName`, `email`). Экран
 * читал несуществующую вложенность и падал целиком, как только участник был хотя
 * бы один; `tsc` молчал, потому что зеркало объявляло себя верным.
 *
 * `id` — ключ УЧАСТИЯ (строки `event_participants`), им адресуется смена статуса;
 * `userId` — ключ пользователя. До 14.09.2026 сервер клал в `id` второе, и действие
 * из строки списка получило бы 404 (BE-половина той же находки).
 */
export type EventAdminParticipant = EventAdminParticipantResponseDto;

/**
 * O-65. Член команды события — генерат BE-контракта, а не рукописное зеркало:
 * прежнее зеркало (`user.firstName`, `role`, `assignedAt`) описывало поля,
 * которых сервер не отдаёт, и tsc этого не видел.
 */
export type EventTeamMember = EventAdminTeamMemberResponseDto;

/**
 * AD-194. Метрики события — генерат BE-контракта, а не рукописное зеркало.
 *
 * Прежнее зеркало описывало ответ, которого сервер никогда не отдавал: вложенный
 * `overview`, `registration.conversionRate`, `engagement` (длительность сессии,
 * просмотры, отказы), `revenue` (билеты, спонсоры), `demographics` (возраст,
 * города). Сервер всё это время отдавал плоские поля, поэтому экран читал
 * `metrics.overview.totalParticipants` — `undefined` — и печатал нули. `tsc` этого
 * не видел: зеркало было объявлено верным по определению.
 *
 * Почему зеркало вообще существовало: до 13.09.2026 форма ответа была инлайновым
 * типом на поле `data`, и генератор описывал её как `{ [key: string]: unknown }` —
 * читать генерат было нечем. Форма вынесена в классы на BE, схема стала
 * конкретной, зеркало снято.
 */
export type EventAdminMetrics = EventAdminMetricsDataDto;

/**
 * AD-188. Уведомление события — генерат BE-контракта, а не рукописное зеркало.
 *
 * Прежнее зеркало путало ТИП уведомления с КАНАЛОМ доставки (`type:
 * "email" | "push" | "sms"`), а сервер отдаёт в `type` повод рассылки
 * (`event_update`, `reminder`, …), каналы же — отдельным массивом `channels`.
 * Дальше расходилось всё: `subject` вместо `title`, `recipients` вместо
 * `recipientsCount`, три статуса вместо пяти (`sending` зеркало не знало вовсе).
 * Экран рисовал колонки «Тема» и «Получателей» по полям, которых в ответе нет.
 */
export type EventNotification = EventAdminNotificationResponseDto;

/**
 * AD-188. Элемент очереди модерации — генерат BE-контракта.
 *
 * Прежнее зеркало описывало посты, комментарии, медиа и вопросы с приоритетом и
 * жалобщиком. Ничего этого сервер не отдаёт: поле называется `contentType`,
 * приоритета и `reportedBy` в ответе нет. Список тоже приходит полем `items`, а
 * зеркало ждало `content` — вкладка поэтому всегда показывала «нет контента»,
 * даже когда очередь была не пуста.
 */
export type EventModerationItem = EventAdminModerationItemResponseDto;

export interface EventSettings {
  eventId: string;
  registrationSettings: {
    isOpen: boolean;
    maxParticipants: number;
    registrationDeadline: string;
    requiresApproval: boolean;
  };
  notificationSettings: {
    emailNotifications: boolean;
    pushNotifications: boolean;
    smsNotifications: boolean;
  };
  moderationSettings: {
    autoApprove: boolean;
    requireModeration: boolean;
    moderationRules: string[];
  };
  privacySettings: {
    isPublic: boolean;
    showParticipants: boolean;
    allowSharing: boolean;
  };
}

/**
 * AD-188. Параметры списка участников — генерат BE-контракта.
 *
 * Рукописное зеркало описывало четыре статуса (`pending|confirmed|cancelled|waitlist`)
 * и четыре роли (`participant|speaker|moderator|volunteer`) — набор, которого у
 * сервера нет: статусов девять (`waitlisted`, не `waitlist`), роль — свободная
 * строка. Фильтр по «waitlist» уходил бы на сервер значением вне enum, а tsc считал
 * бы его единственно верным.
 */
export type ParticipantsQuery = EventAdminGetParticipantsData["query"];

export type TeamQuery = EventAdminGetTeamData["query"];

export interface MetricsQuery {
  eventId: string;
  period?: "day" | "week" | "month" | "year";
  startDate?: string;
  endDate?: string;
}

/** AD-188. Параметры списка уведомлений — генерат BE-контракта (см.
 *  EventNotification: зеркало фильтровало по каналу, считая его типом). */
export type NotificationsQuery = EventAdminGetNotificationsData["query"];

/** AD-188. Параметры очереди модерации — генерат BE-контракта: фильтр
 *  называется `contentType`, а приоритета у сервера нет вовсе. */
export type ModerationQuery = EventAdminGetModerationItemsData["query"];

export interface ReportsQuery {
  eventId: string;
  reportType: "participants" | "revenue" | "engagement" | "feedback";
  format?: "json" | "csv" | "pdf";
  startDate?: string;
  endDate?: string;
}

export interface BackupQuery {
  eventId: string;
  includeParticipants: boolean;
  includeContent: boolean;
  includeAnalytics: boolean;
  format: "json" | "zip";
}

// Request DTO
/**
 * AD-188. Тело смены статуса — генерат BE-контракта.
 *
 * Прежнее зеркало предлагало поля `reason`/`notes`, которых сервер не читает
 * (он ждёт `adminNotes`/`rejectionReason`), и четыре статуса вместо девяти.
 */
export type UpdateParticipantStatusDto = GeneratedUpdateParticipantStatusDto;

/**
 * AD-188. Тело создания уведомления — генерат BE-контракта.
 *
 * Прежнее зеркало слало `subject`, `type: "email"`, `recipients: { … }` и
 * `scheduledFor`. Сервер ждёт `title`, `type` из списка поводов, `channels`,
 * `recipientIds`, `sendImmediately`, `scheduledAt` — и отверг бы такое тело
 * целиком (`forbidNonWhitelisted`), если бы кнопка «Отправить» была подключена.
 */
export type CreateEventNotificationDto = GeneratedCreateEventNotificationDto;

/**
 * AD-188. Тело действия модерации — генерат BE-контракта: действий шесть, а не
 * четыре, и «flag» среди них нет (зеркало предлагало именно его).
 */
export type ModerationActionDto = ModerateContentDto;

// ===== EVENT ADMIN API METHODS =====

export const eventAdminApi = {
  // ==================== УПРАВЛЕНИЕ УЧАСТНИКАМИ ====================

  /**
   * Получить список участников события
   */
  getParticipants: async (
    query: ParticipantsQuery,
  ): Promise<
    ApiResponse<{
      participants: EventAdminParticipant[];
      total: number;
      page: number;
      limit: number;
      totalPages: number;
    }>
  > => {
    return httpClient.get(
      `/event-admin/participants${buildQueryString(query)}`,
    );
  },

  /**
   * Обновить статус участника
   */
  /**
   * AD-188: `eventId` — обязательный параметр запроса у BE
   * (`@Query('eventId')` в `event-admin.controller.ts`), без него сервер не может
   * ни найти строку участия, ни проверить права на событие. Клиент его не слал.
   */
  updateParticipantStatus: async (
    participantId: string,
    eventId: string,
    data: UpdateParticipantStatusDto,
  ): Promise<ApiResponse<{ success: boolean; message: string }>> => {
    return httpClient.put(
      `/event-admin/participants/${participantId}/status${buildQueryString({
        eventId,
      })}`,
      data,
    );
  },

  // ==================== УПРАВЛЕНИЕ КОМАНДОЙ ====================

  /**
   * Получить команду организаторов события
   */
  getTeam: async (
    query: TeamQuery,
  ): Promise<ApiResponse<EventAdminTeamResponseDto>> => {
    return httpClient.get(`/event-admin/team${buildQueryString(query)}`);
  },

  // ==================== МЕТРИКИ И АНАЛИТИКА ====================

  /**
   * Получить метрики события
   */
  getMetrics: async (
    query: MetricsQuery,
  ): Promise<ApiResponse<EventAdminMetrics>> => {
    return httpClient.get<EventAdminMetrics>(
      `/event-admin/metrics${buildQueryString(query)}`,
    );
  },

  // ==================== УВЕДОМЛЕНИЯ ====================

  /**
   * Получить уведомления события
   */
  getNotifications: async (
    query: NotificationsQuery,
  ): Promise<ApiResponse<EventAdminNotificationsResponseDto>> => {
    return httpClient.get(
      `/event-admin/notifications${buildQueryString(query)}`,
    );
  },

  /**
   * Создать уведомление для участников
   *
   * AD-188: `eventId` — параметр запроса (`@Query('eventId')`), а не поле тела;
   * прежний клиент клал его в тело, где сервер его не читает.
   */
  createNotification: async (
    eventId: string,
    data: CreateEventNotificationDto,
  ): Promise<ApiResponse<{ success: boolean; message: string }>> => {
    return httpClient.post(
      `/event-admin/notifications${buildQueryString({ eventId })}`,
      data,
    );
  },

  // ==================== МОДЕРАЦИЯ КОНТЕНТА ====================

  /**
   * Получить контент для модерации
   */
  getModerationItems: async (
    query: ModerationQuery,
  ): Promise<ApiResponse<EventAdminModerationResponseDto>> => {
    return httpClient.get(`/event-admin/moderation${buildQueryString(query)}`);
  },

  /**
   * Выполнить действие модерации
   *
   * AD-188: маршрут — `PUT /event-admin/moderation/:contentId` с обязательным
   * `eventId` в запросе. Клиент звал `/:contentId/action` — такого маршрута у
   * сервера нет, то есть любое действие модерации возвращало 404.
   */
  performModerationAction: async (
    contentId: string,
    eventId: string,
    data: ModerationActionDto,
  ): Promise<ApiResponse<{ success: boolean; message: string }>> => {
    return httpClient.put(
      `/event-admin/moderation/${contentId}${buildQueryString({ eventId })}`,
      data,
    );
  },

  // ==================== ОТЧЕТЫ ====================

  /**
   * Получить отчеты события
   */
  getReports: async (query: ReportsQuery): Promise<ApiResponse<any>> => {
    const queryString = buildQueryString(query);

    // Для файлов возвращаем blob
    if (query.format === "csv" || query.format === "pdf") {
      // TODO: типизировать ответ getReports (blob vs json) после обновления контракта (при необходимости — TASK-EXT-01)
      return httpClient.get(`/event-admin/reports${queryString}`, {
        responseType: "blob",
      }) as unknown as ReturnType<typeof httpClient.get>;
    }

    return httpClient.get(`/event-admin/reports${queryString}`);
  },

  // ==================== НАСТРОЙКИ СОБЫТИЯ ====================

  /**
   * Получить настройки события
   */
  getSettings: async (eventId: string): Promise<ApiResponse<EventSettings>> => {
    return httpClient.get<EventSettings>(
      `/event-admin/settings${buildQueryString({ eventId })}`,
    );
  },

  /**
   * Обновить настройки события
   */
  updateSettings: async (
    data: EventSettings,
  ): Promise<
    ApiResponse<{
      success: boolean;
      message: string;
    }>
  > => {
    return httpClient.put("/event-admin/settings", data);
  },

  // ==================== РЕЗЕРВНОЕ КОПИРОВАНИЕ ====================

  /**
   * Создать резервную копию данных события
   */
  createBackup: async (query: BackupQuery): Promise<ApiResponse<any>> => {
    // TODO: типизировать ответ createBackup (blob) после обновления контракта (при необходимости — TASK-EXT-01)
    return httpClient.get(`/event-admin/backup${buildQueryString(query)}`, {
      responseType: "blob",
    }) as unknown as Promise<ApiResponse<Blob>>;
  },
};

export default eventAdminApi;
